import { cva } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "../../lib/cn";

const alertVariants = cva("my-3 rounded-md border px-3 py-2 text-sm", {
  variants: {
    kind: {
      error: "border-destructive/50 text-destructive bg-destructive/5",
      success: "border-success/50 text-success bg-success/5",
      info: "border-primary/40 bg-primary/5",
    },
  },
});

/**
 * Errors are announced assertively (role alert), the rest politely (role status).
 *
 * The remaining attributes are forwarded rather than dropped. An earlier version took only
 * these three props, so a `data-testid` on an Alert did nothing — and TypeScript exempts
 * hyphenated JSX attributes from excess-property checks, so it typechecked silently and
 * the test looking for it simply never found it.
 */
export function Alert({
  kind,
  children,
  className,
  ...rest
}: {
  kind: "error" | "success" | "info";
  children: ReactNode;
  className?: string;
} & Omit<HTMLAttributes<HTMLParagraphElement>, "children" | "className">) {
  return (
    <p
      role={kind === "error" ? "alert" : "status"}
      className={cn(alertVariants({ kind }), className)}
      {...rest}
    >
      {children}
    </p>
  );
}
